import type {
  AnswerEvidenceInput,
  CommissionSummaryDto,
  PortalStatementDetailDto,
  PortalStatementDto,
  PortalCommissionLineDto,
  ApiErrorBody,
  CreateRegistrationInput,
  PortalMeDto,
  PortalRegistrationDto,
  RegistrationCountsDto,
  UpdateRegistrationInput,
  RequestLoginLinkInput,
  ReportStageInput,
  UpdatePortalMeInput,
} from '@wally-reseller/shared';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: ApiErrorBody,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Portal API: the session cookie is the credential (same origin, HttpOnly). */
async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api/portal${path}`, {
    method,
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
    credentials: 'same-origin',
  });
  if (res.status === 204) return undefined as T;
  const data = (await res.json().catch(() => null)) as unknown;
  if (!res.ok) {
    const err = data as ApiErrorBody | null;
    const message = Array.isArray(err?.message) ? err.message.join(', ') : err?.message;
    throw new ApiError(res.status, message ?? res.statusText, err ?? undefined);
  }
  return data as T;
}

export const api = {
  requestLink: (input: RequestLoginLinkInput) => request<void>('POST', '/auth/link', input),
  verify: (token: string) => request<PortalMeDto>('POST', '/auth/verify', { token }),
  logout: () => request<void>('POST', '/auth/logout'),
  me: () => request<PortalMeDto>('GET', '/me'),
  updateMe: (input: UpdatePortalMeInput) => request<PortalMeDto>('PATCH', '/me', input),
  switchEntity: (partnerId: string) => request<PortalMeDto>('POST', '/me/entity', { partnerId }),

  registrations: () => request<PortalRegistrationDto[]>('GET', '/registrations'),
  registrationCounts: () => request<RegistrationCountsDto>('GET', '/registrations/counts'),
  registration: (id: string) => request<PortalRegistrationDto>('GET', `/registrations/${id}`),
  register: (input: CreateRegistrationInput) =>
    request<PortalRegistrationDto>('POST', '/registrations', input),
  updateRegistration: (id: string, input: UpdateRegistrationInput) =>
    request<PortalRegistrationDto>('PATCH', `/registrations/${id}`, input),
  withdraw: (id: string) => request<PortalRegistrationDto>('POST', `/registrations/${id}/withdraw`),
  requestReason: (id: string) =>
    request<PortalRegistrationDto>('POST', `/registrations/${id}/reason-request`),
  reportStage: (id: string, input: ReportStageInput) =>
    request<PortalRegistrationDto>('POST', `/registrations/${id}/stage`, input),
  answerEvidence: (id: string, input: AnswerEvidenceInput) =>
    request<PortalRegistrationDto>('POST', `/registrations/${id}/evidence`, input),

  commissionSummary: () => request<CommissionSummaryDto>('GET', '/commission/summary'),
  registrationCommission: (id: string) =>
    request<PortalCommissionLineDto[]>('GET', `/registrations/${id}/commission`),

  statements: () => request<PortalStatementDto[]>('GET', '/statements'),
  statement: (id: string) => request<PortalStatementDetailDto>('GET', `/statements/${id}`),
};
